import React, { useState, useEffect } from 'react';
import { Music, Mail, Phone, Calendar, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const BeatRequestsReader = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'completed'

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/beat-requests`);
      setRequests(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching beat requests:', error);
      toast.error('Failed to load beat requests');
      setLoading(false);
    }
  };

  const markAsCompleted = async (requestId) => {
    try {
      await axios.put(`${API}/beat-requests/${requestId}/complete`);
      toast.success('Request marked as completed');
      fetchRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Failed to update request');
    }
  };

  const deleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;

    try {
      await axios.delete(`${API}/beat-requests/${requestId}`);
      toast.success('Request deleted');
      fetchRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request');
    }
  };

  const getFilteredRequests = () => {
    switch (filter) {
      case 'pending':
        return requests.filter(r => !r.completed);
      case 'completed':
        return requests.filter(r => r.completed);
      default:
        return requests;
    }
  };

  const filteredRequests = getFilteredRequests();
  const pendingCount = requests.filter(r => !r.completed).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <Music className="text-purple-400" />
            Beat Requests ({filteredRequests.length})
          </h3>
          <p className="text-gray-400 text-sm mt-1">
            {pendingCount} pending • {requests.length - pendingCount} completed
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              filter === 'all'
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                : 'bg-purple-900/20 text-gray-400 hover:text-white'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              filter === 'pending'
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                : 'bg-purple-900/20 text-gray-400 hover:text-white'
            }`}
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('completed')}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              filter === 'completed'
                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                : 'bg-purple-900/20 text-gray-400 hover:text-white'
            }`}
          >
            Completed
          </button>
        </div>
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <div className="bg-purple-900/10 border border-purple-500/20 rounded-lg p-12 text-center">
          <Music size={64} className="mx-auto text-gray-500 mb-4" />
          <p className="text-gray-400 text-lg">No beat requests yet</p>
          <p className="text-gray-500 text-sm mt-2">Requests will appear here when users submit them</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredRequests.map((request) => (
            <div
              key={request.id}
              className={`bg-gradient-to-br from-purple-900/20 to-black/40 border rounded-lg p-6 transition-all hover:border-purple-500/50 ${
                request.completed
                  ? 'border-green-500/30 opacity-75'
                  : 'border-purple-500/30'
              }`}
            >
              <div className="flex items-start justify-between flex-wrap gap-4">
                {/* Left Side - Request Info */}
                <div className="flex-1 space-y-3">
                  {/* Header */}
                  <div className="flex items-center gap-3">
                    <Music size={24} className={request.completed ? 'text-green-400' : 'text-purple-400'} />
                    <h4 className="text-xl font-bold text-white">
                      {request.name || 'Anonymous'}
                    </h4>
                    {request.completed && (
                      <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm font-semibold">
                        Completed
                      </span>
                    )}
                  </div>

                  {/* Contact Info */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-gray-300">
                      <Mail size={16} />
                      <span>{request.email}</span>
                    </div>
                    {request.phone && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Phone size={16} />
                        <span>{request.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar size={16} />
                      <span>
                        {new Date(request.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Request Details */}
                  <div className="bg-black/40 rounded-lg p-4 space-y-2">
                    {request.beat_type && (
                      <div>
                        <span className="text-purple-400 font-semibold">Type: </span>
                        <span className="text-white">{request.beat_type}</span>
                      </div>
                    )}
                    {request.description && (
                      <div>
                        <span className="text-purple-400 font-semibold">Description: </span>
                        <p className="text-gray-300 mt-1">{request.description}</p>
                      </div>
                    )}
                    {request.reference && (
                      <div>
                        <span className="text-purple-400 font-semibold">Reference: </span>
                        <span className="text-gray-300">{request.reference}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Side - Actions */}
                <div className="flex flex-col gap-2">
                  {!request.completed && (
                    <button
                      onClick={() => markAsCompleted(request.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 font-semibold"
                    >
                      <Check size={18} />
                      Complete
                    </button>
                  )}
                  <button
                    onClick={() => deleteRequest(request.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 font-semibold"
                  >
                    <X size={18} />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BeatRequestsReader;
